import { useEffect, useState } from "react";
import { bookHref } from "@/utils/booking";
import { Phone, Calendar } from "lucide-react";
import { trackEvent } from "@/utils/tracking";

const STICKY_BOOK_URL = bookHref({ campaign: "sticky-cta", content: "sticky-book" });

/**
 * Sticky CTA
 * - Mobile: full-width bar fixed at the very bottom with Call + Book side-by-side
 * - Desktop: bottom-left floating pill (Tidio occupies bottom-right)
 * - Appears after scrolling past ~500px
 * - Hides itself when the contact section is in view (don't stack with inline form CTAs)
 */
export default function StickyCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY > 500;
      const contactEl = document.getElementById("contact");
      let contactInView = false;
      if (contactEl) {
        const r = contactEl.getBoundingClientRect();
        contactInView = r.top < window.innerHeight * 0.8 && r.bottom > 0;
      }
      setVisible(scrolled && !contactInView);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Mobile bottom bar */}
      <div
        data-testid="sticky-cta-mobile"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 flex border-t border-white/10 bg-[#050A11]/95 backdrop-blur-xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <a
          href="tel:2398344377"
          onClick={() => trackEvent("sticky_cta_click", { action: "call", device: "mobile" })}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 text-white font-medium text-sm border-r border-white/10 hover:bg-white/5 transition-colors"
        >
          <Phone size={16} className="text-blue-400" /> Call
        </a>
        <a
          href={STICKY_BOOK_URL}
          onClick={() => trackEvent("sticky_cta_click", { action: "book", device: "mobile" })}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 transition-colors"
        >
          <Calendar size={16} /> Book Now
        </a>
      </div>

      {/* Desktop floating pill (bottom-left to avoid Tidio) */}
      <a
        href={STICKY_BOOK_URL}
        data-testid="sticky-cta-desktop"
        onClick={() => trackEvent("sticky_cta_click", { action: "book", device: "desktop" })}
        className="hidden md:inline-flex fixed bottom-6 left-6 z-40 items-center gap-2.5 pl-4 pr-5 py-3.5 rounded-full bg-blue-600 text-white font-medium text-sm shadow-[0_10px_40px_-10px_rgba(37,99,235,0.8)] border border-blue-400/30 hover:bg-blue-500 hover:scale-[1.03] transition-all btn-glow"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
        </span>
        Book a Consultation
      </a>
    </>
  );
}
